/**
 * C2 citation-revocation production data SDK (Bundle 1 C2 data feed).
 *
 * Thin shell-V1 fetchers + a pure mapper that lifts backend Pydantic shapes
 * into the chip module's `CitationView` adapter seam. Mirrors the per-feature
 * SDK pattern (bridge/audit.ts) — injectable `fetchImpl` + configurable
 * `apiBase` for testability; no hidden globals.
 *
 * Backend contracts (do NOT invent — see workspace_backend/app/api/):
 *   GET /api/workspace/artifacts/{artifact_id}
 *     → ArtifactResponse  (status='REVOKED' triggers chip; tombstone fields)
 *     → 404 ARTIFACT_NOT_FOUND when unknown (mapped to `null` by this SDK)
 *
 *   GET /api/workspace/artifact_citations/by-artifact/{artifact_id}
 *     → CitationResponse[]  (citation_id + ack/escalate timestamps)
 *
 * Spec ref: `_AEGIS/knowledge/00_Velocity_Workspace/02_specs/
 *           05_artifact_lifecycle_and_citations_2026-05-31.md` §5-§6, §9.
 *
 * Action wiring (POST acknowledge/escalate) is intentionally NOT included in
 * this SDK — that is a separate wave step. Producing `CitationView` here is
 * sufficient for the chip's READ/DISPLAY path; the chip's `onAction` prop
 * remains caller-owned.
 */
import type {
  ArtifactStatus,
  CitationAckStatus,
  CitationView,
} from '../types'

// ── Backend response shapes (mirror Pydantic DTOs) ──────────────────────────

/**
 * Mirrors `workspace_backend/app/api/artifacts.py::ArtifactResponse`.
 * Backend `status` is the SQL enum string ('DRAFT' | 'PUBLISHED' | 'REVOKED');
 * mapping to the chip's narrower `ArtifactStatus` happens in `toCitationView`.
 */
export interface ArtifactBackendResponse {
  id: string
  owner_app: string
  artifact_type: string
  title: string
  status: 'DRAFT' | 'PUBLISHED' | 'REVOKED'
  revoked_at: string | null
  revoked_by: string | null
  revoked_reason: string | null
  tombstone: unknown
}

/**
 * Mirrors `workspace_backend/app/api/artifact_citations.py::CitationResponse`.
 * `tombstone_*` ISO timestamps are nullable; ack-state derivation is in
 * `toCitationView`.
 */
export interface CitationBackendResponse {
  id: string
  artifact_id: string
  citing_app: string
  citing_entity_type: string
  citing_entity_id: string
  citing_user_id: string | null
  citation_context: string | null
  tombstone_acknowledged_at: string | null
  tombstone_escalated_to_owner_at: string | null
}

// ── Client config ──────────────────────────────────────────────────────────

export interface CitationRevocationApiOptions {
  /** Workspace API base, e.g. `/api/workspace`. Required (no implicit default). */
  apiBase: string
  /** Optional fetch override for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch
}

// ── Fetchers ───────────────────────────────────────────────────────────────

/**
 * GET /artifacts/{artifact_id}. Returns the parsed response, or `null` when
 * the backend returns 404 (`ARTIFACT_NOT_FOUND`). Other non-2xx responses
 * throw — callers (or react-query) decide how to surface those.
 */
export async function fetchArtifactView(
  artifactId: string,
  opts: CitationRevocationApiOptions,
): Promise<ArtifactBackendResponse | null> {
  const f = opts.fetchImpl ?? fetch
  const base = opts.apiBase.replace(/\/$/, '')
  const url = `${base}/artifacts/${encodeURIComponent(artifactId)}`
  const res = await f(url, { method: 'GET' })
  if (res.status === 404) return null
  if (!res.ok) {
    throw new Error(
      `fetchArtifactView ${artifactId}: HTTP ${res.status} ${res.statusText}`,
    )
  }
  return (await res.json()) as ArtifactBackendResponse
}

/**
 * GET /artifact_citations/by-artifact/{artifact_id}. Returns the list as-is
 * (may be empty). Non-2xx throws.
 */
export async function fetchCitationsForArtifact(
  artifactId: string,
  opts: CitationRevocationApiOptions,
): Promise<CitationBackendResponse[]> {
  const f = opts.fetchImpl ?? fetch
  const base = opts.apiBase.replace(/\/$/, '')
  const url = `${base}/artifact_citations/by-artifact/${encodeURIComponent(artifactId)}`
  const res = await f(url, { method: 'GET' })
  if (!res.ok) {
    throw new Error(
      `fetchCitationsForArtifact ${artifactId}: HTTP ${res.status} ${res.statusText}`,
    )
  }
  return (await res.json()) as CitationBackendResponse[]
}

// ── Pure mapper ────────────────────────────────────────────────────────────

/**
 * Lift a backend (Artifact, Citation) pair into the chip module's
 * `CitationView`. Returns `null` when no chip should render — either there's
 * no matching citation, or the artifact is not in a chip-worthy state.
 *
 * Mapping rules:
 *   - backend status 'DRAFT' → null (no citation chip for drafts)
 *   - backend status 'PUBLISHED' → CitationView with `artifact_status='published'`
 *     (chip self-renders null via deriveChipState; included so callers can
 *     still introspect, e.g. for active-state badges later)
 *   - backend status 'REVOKED' → CitationView with `artifact_status='revoked'`
 *
 * Ack-status derivation (spec §6):
 *   - tombstone_escalated_to_owner_at != null  → 'escalated'
 *   - tombstone_acknowledged_at != null         → 'acknowledged'
 *   - otherwise                                  → 'pending'
 *
 * `escalation_due_at` is intentionally OMITTED: the backend tracks escalation
 * explicitly via the timestamp above, NOT via a time-window auto-promotion.
 * deriveChipState's auto-promote branch is conditional on the field being
 * defined and stays inert when absent.
 */
export function toCitationView(
  artifact: ArtifactBackendResponse,
  citation: CitationBackendResponse,
): CitationView | null {
  if (artifact.status === 'DRAFT') return null

  const artifactStatus: ArtifactStatus =
    artifact.status === 'REVOKED' ? 'revoked' : 'published'

  const ackStatus: CitationAckStatus =
    citation.tombstone_escalated_to_owner_at !== null
      ? 'escalated'
      : citation.tombstone_acknowledged_at !== null
        ? 'acknowledged'
        : 'pending'

  const view: CitationView = {
    citation_id: citation.id,
    artifact_id: artifact.id,
    artifact_title: artifact.title,
    artifact_status: artifactStatus,
    ack_status: ackStatus,
  }

  if (artifact.revoked_reason !== null) {
    view.artifact_revocation_reason = artifact.revoked_reason
  }

  if (artifact.revoked_at !== null) {
    const ms = Date.parse(artifact.revoked_at)
    if (!Number.isNaN(ms)) view.revoked_at = ms
  }

  return view
}
